import { useCallback, useMemo, useState } from 'react';
import { getLocalTimeZone, parseDate, today, type CalendarDate, type DateValue } from '@internationalized/date';

const KEY = 'analyst-dashboard-date-range';

function sevenDayRange() {
  const dateTo = today(getLocalTimeZone());
  return { dateFrom: dateTo.subtract({ days: 7 }), dateTo };
}

function readStored(): { dateFrom: CalendarDate; dateTo: CalendarDate } | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { dateFrom: parseDate(parsed.dateFrom), dateTo: parseDate(parsed.dateTo) };
  } catch {
    return null;
  }
}

function toDate(value: DateValue) {
  return parseDate(value.toString().slice(0, 10));
}

/** Session date range for Analyst dashboards. Empty session falls back to 7 days, or `configured` when set. */
export function useAnalystDashboardDateRange(configured?: { dateFrom: CalendarDate; dateTo: CalendarDate } | null) {
  const defaults = useMemo(sevenDayRange, []);
  const [stored, setStored] = useState(readStored);
  const dateFrom = stored?.dateFrom ?? configured?.dateFrom ?? defaults.dateFrom;
  const dateTo = stored?.dateTo ?? configured?.dateTo ?? defaults.dateTo;

  const save = useCallback((nextFrom: CalendarDate, nextTo: CalendarDate) => {
    sessionStorage.setItem(KEY, JSON.stringify({ dateFrom: nextFrom.toString(), dateTo: nextTo.toString() }));
    setStored({ dateFrom: nextFrom, dateTo: nextTo });
  }, []);

  const setDateFrom = useCallback((date: DateValue | null) => {
    save(date ? toDate(date) : sevenDayRange().dateFrom, dateTo);
  }, [dateTo, save]);

  const setDateTo = useCallback((date: DateValue | null) => {
    save(dateFrom, date ? toDate(date) : sevenDayRange().dateTo);
  }, [dateFrom, save]);

  const setDateRange = useCallback((from: DateValue, to: DateValue) => {
    save(toDate(from), toDate(to));
  }, [save]);

  return { dateFrom, dateTo, setDateFrom, setDateTo, setDateRange };
}
